import { logger } from '@gem/modules/logger';

import { Listener, Stream } from './Stream';

export enum WsEventType {
  Message = 'message',
  Error = 'error',
  Close = 'close',
  Open = 'open',
}

export enum WsDisconnectCode {
  ManualClose = 1000,
  TokenExpired = 4401,
  NetworkError = 1006,
  UnexpectedError = 1011,
}

type WsEventMap = {
  [WsEventType.Message]: MessageEvent;
  [WsEventType.Error]: Event;
  [WsEventType.Close]: CloseEvent;
  [WsEventType.Open]: Event;
};

type StreamMap<T extends WsEventType> = Record<T, Stream<WsEventMap[T]>>;

export class WebSocketService {
  ws: WebSocket;
  public isClosing = false;
  private streams: StreamMap<WsEventType> = {
    [WsEventType.Message]: new Stream<MessageEvent>(),
    [WsEventType.Error]: new Stream<Event>(),
    [WsEventType.Close]: new Stream<CloseEvent>(),
    [WsEventType.Open]: new Stream<Event>(),
  };

  connect(url: string, binaryType: BinaryType = 'blob'): void {
    if (this.isConnected) {
      logger.log('socket has already opened');
      return;
    }
    this.ws = new WebSocket(url);
    this.ws.binaryType = binaryType;

    this.ws.onopen = (event: Event): void => {
      this.streams[WsEventType.Open].emit(event);
    };

    this.ws.onerror = (event: Event): void => {
      this.streams[WsEventType.Error].emit(event);
    };

    this.ws.onmessage = (event: MessageEvent): void => {
      this.streams[WsEventType.Message].emit(event);
    };

    this.ws.onclose = (event: CloseEvent): void => {
      console.log('CLOSED', event);
      this.streams[WsEventType.Close].emit(event);
      this.isClosing = false;
    };
  }

  public get isConnected(): boolean {
    return (
      this.ws && (this.ws.readyState === this.ws.OPEN || this.ws.readyState === this.ws.CONNECTING)
    );
  }

  public on<T extends WsEventType>(type: T, listener: Listener<WsEventMap[T]>): () => void {
    return this.streams[type].on(listener);
  }

  public off<T extends WsEventType>(type: T, listener: Listener<WsEventMap[T]>): void {
    this.streams[type].off(listener);
  }

  public get readyState(): number {
    return this.ws.readyState;
  }

  send(message: string | ArrayBufferView): boolean {
    if (!this.ws || this.ws.readyState !== this.ws.OPEN) {
      return false;
    }

    this.ws.send(message);

    return true;
  }

  disconnect(code?: WsDisconnectCode): void {
    this.isClosing = true;
    this.ws.close(code);
  }
}
